import React, { useState, useEffect, useCallback } from 'react';
import { CallBtn, WABtn } from '../components/ui/PhoneButtons';
import { supabase } from '../lib/supabase';
import ImportButton from '../components/ui/ImportButton';
import { useAuth } from '../hooks/useAuth';
import useLiveRefresh from '../hooks/useLiveRefresh';
import toast from 'react-hot-toast';
import { Plus, X, Search, GraduationCap, Eye, Edit2, Trash2 } from 'lucide-react';
import SearchableSelect from '../components/ui/SearchableSelect';
import { saveStyledXlsx } from '../lib/excelStyle';
import DateInput from '../components/ui/DateInput';
import { fmtDMY } from '../lib/dateFormat';

// PostgREST returns max 1000 rows per request — page anything that can grow beyond that.
async function fetchAllPaged(buildQuery) {
  let all = [], from = 0;
  while (true) {
    const { data, error } = await buildQuery().range(from, from + 999);
    if (error) throw error;
    all = all.concat(data || []);
    if (!data || data.length < 1000) break;
    from += 1000;
  }
  return all;
}
// Centre / stream were typed in many spellings (Kochi, Cochin, Ernakulam, MECH …) — compare by a normalised name.
const normCentre = c => {
  const v = String(c || '').trim().toLowerCase();
  if (!v) return '';
  if (['ekm', 'kochi', 'cochin', 'ernakulam'].includes(v)) return 'EKM';
  if (['tvm', 'trivandrum', 'thiruvananthapuram'].includes(v)) return 'TVM';
  return String(c).trim();
};
const normStream = s => {
  const v = String(s || '').trim();
  if (!v) return '';
  const l = v.toLowerCase();
  if (l === 'mech') return 'Mechanical';
  const hit = STREAMS.find(x => x.toLowerCase() === l);
  return hit || v.split(/\s+/).map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
};
const isCertIssued = i => !!(i.cert_issued_date || i.certificate_collected_date);

const STREAMS = ['Civil', 'Mechanical', 'Electrical', 'Automobile', 'Mechatronics', 'Chemical', 'Electronics', 'Computer Science', 'Information Technology', 'Other'];
const CENTRES = ['EKM', 'TVM'];
const TRAINERS = ['Aravind', 'Anoop Chandran', 'Piyush', 'Nithin Raj', 'Adarsh', 'Vishnu', 'Ashmi', 'Anumon', 'Imthisam'];
const SEMESTERS = ['1', '2', '3', '4', '5', '6', '7', '8', 'Passed Out'];

const PAYMENT_TYPES_LIST = ['Bank Transfer','Cash','Cheque','UPI'];
const PAYMENT_MODES_LIST = ['EKM FDO','EKM Admin','TVM FDO','TVM Admin','Cash','HDFC Vazhakkala','HDFC Kottarakkara','SBI','Ujjivan'];
const PAYMENT_CATS_LIST  = ['Tuition Fees','Hostel Fees','Exam Fees','Manpower Fees','Registration Fees','Salary Recovery','Other Income','Salary','Salary Advance','TA (Travel Allowance)','Food Expenses','Marketing','Rent','Utilities','Office Expenses','Miscellaneous'];



const emptyForm = {
  // Personal
  student_name: '', guardian_name: '', contact_number: '', whatsapp_number: '',
  college_id: '', semester: '', passed_out_year: '', stream: '',
  // Internship
  centre: '', start_date: '', end_date: '', num_days: '',
  topics_covered: '', trainer: '',  // text field
  demo_provided: '', demo_provided_by: '',
  hostel_provided: '', certificate_start_date: '', certificate_end_date: '',
  certificate_duration: '', certificate_collected_date: '',
  collected_person_name: '', certificate_issuer: '',
  // Admission
  admission_taken: '', batch_id: '', remarks: '',
  // Payment
  paid_amount: '', paid_date: '', payment_type: 'Bank Transfer',
  payment_mode: '', payment_category: '', receipt_no: '', add_to_accounts: 'N',
  // Certificate Issued
  cert_issued_date: '', cert_issued_by: '',
};

const emptyVisitForm = { college_name:'', college_id:'', centre:'', visit_date:'', enquiry_date:'', stream:'', semester:'', passout_year:'', fees_collected:'', faculty_taken_class:'', remarks:'', coordinator_id:'' };
const emptyVisitStudent = { student_name:'', contact_number:'', stream:'', semester:'', passout_year:'' };

function sortBatches(batches) {
  // Ongoing first, then Upcoming, then Completed; inside each group the newest batch (latest start date) comes first.
  const rank = s => { const v = String(s || '').trim().toLowerCase(); return (v === 'ongoing' || v === 'active' || v === 'running') ? 0 : (v === 'completed' || v === 'closed') ? 2 : 1; };
  return [...(batches || [])].sort((a, b) => {
    const d = rank(a.status) - rank(b.status);
    if (d) return d;
    const sa = a.start_date || '', sb = b.start_date || '';
    if (sa !== sb) return sa < sb ? 1 : -1;
    return (b.batch_name || '').localeCompare(a.batch_name || '', undefined, { numeric: true });
  });
}

export default function Internship() {
  const { isEditor, canEdit, user } = useAuth();

  const [internships, setInternships] = useState([]);
  const [colleges, setColleges]       = useState([]);
  const [batches, setBatches]         = useState([]);
  const [loading, setLoading]         = useState(true);

  const [showModal, setShowModal]     = useState(false);
  const [editId, setEditId]           = useState(null);
  const [form, setForm]               = useState(emptyForm);
  const [formTab, setFormTab]         = useState('personal');
  const [mainTab, setMainTab] = useState('internship');
  const [showVisitModal, setShowVisitModal] = useState(false);
  const [visits, setVisits] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [visitForm, setVisitForm] = useState(emptyVisitForm);
  const [visitStudents, setVisitStudents] = useState([{ ...emptyVisitStudent }]);
  const [editVisitId, setEditVisitId] = useState(null);
  const [savingVisit, setSavingVisit] = useState(false);
  const [viewVisit, setViewVisit] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showReport, setShowReport] = useState(false);
  const [filterAdmission, setFilterAdmission] = useState('');
  const [filterReport, setFilterReport] = useState(''); // centre or stream filter from report
  const [saving, setSaving]           = useState(false);

  const [search, setSearch]             = useState('');
  const [viewItem, setViewItem]         = useState(null);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const toggleSel = id => setSelectedItems(prev => { const n=new Set(prev); n.has(id)?n.delete(id):n.add(id); return n; });
  const [filterCollege, setFilterCollege] = useState('');
  const [filterCentre, setFilterCentre]   = useState('');
  const [filterSemester, setFilterSemester] = useState('');
  const [filterStream, setFilterStream]   = useState('');
  const [filterPassedout, setFilterPassedout] = useState('');
  const [filterTrainer, setFilterTrainer] = useState('');

  useEffect(() => { loadAll(); loadVisits(); }, []);
  useLiveRefresh(['internships'], () => loadInternships(), true, 1500);
  useLiveRefresh(['industrial_visits', 'industrial_visit_students'], () => loadVisits(), true, 1500);

  async function loadVisits() {
    try {
      const data = await fetchAllPaged(() => supabase.from('industrial_visits')
        .select('*, coordinator:staff(full_name), industrial_visit_students(*)')
        .order('visit_date', {ascending:false}).order('id'));
      setVisits(data || []);
    } catch (e) { toast.error('Could not load visits: ' + e.message); }
  }

  async function saveVisit() {
    if (!visitForm.centre) { toast.error('Select the centre'); return; }
    if (!visitForm.college_name.trim()) { toast.error('Enter college name'); return; }
    if (!visitForm.visit_date) { toast.error('Enter visit date'); return; }
    if (visitForm.enquiry_date && visitForm.enquiry_date > visitForm.visit_date && !window.confirm('Enquiry date is after the visit date. Save anyway?')) return;
    setSavingVisit(true);
    try {
      const nz = v => (v === '' || v === undefined ? null : v);
      const payload = {
        college_name: visitForm.college_name.trim(),
        college_id: visitForm.college_id==='__new__' ? null : visitForm.college_id || null,
        centre: nz(visitForm.centre), visit_date: nz(visitForm.visit_date), enquiry_date: nz(visitForm.enquiry_date),
        stream: nz(visitForm.stream), fees_collected: Number(visitForm.fees_collected)||0,
        faculty_taken_class: nz(visitForm.faculty_taken_class), remarks: nz(visitForm.remarks),
        coordinator_id: nz(visitForm.coordinator_id), created_by: user?.email||'',
      };
      if (visitForm.semester) payload.semester = visitForm.semester;
      if (visitForm.passout_year) payload.passout_year = visitForm.passout_year;
      let visitId = editVisitId;
      if (editVisitId) {
        const { error } = await supabase.from('industrial_visits').update(payload).eq('id', editVisitId);
        if (error) throw error;
      } else {
        const { data, error } = await supabase.from('industrial_visits').insert(payload).select().single();
        if (error) throw error;
        visitId = data?.id;
      }
      if (visitId) {
        // Insert the new student rows FIRST, then remove the old ones — a failed insert can no longer wipe the list
        const oldIds = editVisitId ? (visits.find(v => v.id === editVisitId)?.industrial_visit_students || []).map(x => x.id) : [];
        const rows = visitStudents.filter(x => x.student_name.trim()).map(x => ({
          visit_id: visitId, student_name: x.student_name.trim(), contact_number: x.contact_number || null,
          stream: x.stream || null, semester: x.semester || null, passout_year: x.passout_year || null,
        }));
        if (rows.length) {
          const { error } = await supabase.from('industrial_visit_students').insert(rows);
          if (error) throw error;
        }
        if (oldIds.length) {
          const { error } = await supabase.from('industrial_visit_students').delete().in('id', oldIds);
          if (error) toast.error('Saved, but the old student rows could not be removed: ' + error.message);
        }
      }
      toast.success(editVisitId ? 'Visit updated!' : 'Visit saved!');
      setShowVisitModal(false);
      setEditVisitId(null);
      setVisitForm(emptyVisitForm);
      setVisitStudents([{ ...emptyVisitStudent }]);
      loadVisits();
    } catch (e) { toast.error('Could not save visit: ' + e.message); }
    setSavingVisit(false);
  }

  async function deleteVisit(v) {
    if (!window.confirm(`Delete the visit to "${v.college_name}" and its ${v.industrial_visit_students?.length || 0} student rows?`)) return;
    const { error: e1 } = await supabase.from('industrial_visit_students').delete().eq('visit_id', v.id);
    if (e1) { toast.error(e1.message); return; }
    const { error } = await supabase.from('industrial_visits').delete().eq('id', v.id);
    if (error) { toast.error(error.message); return; }
    toast.success('Visit deleted');
    loadVisits();
  }

  const loadInternships = useCallback(async () => {
    try {
    const data = await fetchAllPaged(() => supabase
      .from('internships')
      .select('id, student_name, guardian_name, contact_number, whatsapp_number, college_name, college_id, stream, centre, start_date, end_date, num_days, demo_provided, demo_provided_by, hostel_provided, admission_taken, trainer, trainer_name, passedout_year, passed_out_year, semester, batch_name, batch_id, topics_covered, created_at, cert_issued_date, cert_issued_by, certificate_start_date, certificate_end_date, certificate_duration, certificate_collected_date, collected_person_name, certificate_issuer, remarks')
      .order('created_at', { ascending: false }).order('id'));
    setInternships(data || []);
    } catch (e) { toast.error('Could not load internships: ' + e.message); }
  }, []);

  async function loadAll() {
    try {
    const [c, b, st] = await Promise.all([
      supabase.from('colleges').select('id, college_name').order('college_name'),
      supabase.from('batches').select('id, batch_name, batch_code, status, start_date').order('batch_name'),
      supabase.from('staff').select('id, full_name').eq('is_active', true).order('full_name'),
    ]);
    setColleges(c.data || []);
    setBatches(sortBatches(b.data || []));
    setStaffList(st.data || []);
    await loadInternships();
    } catch (e) { toast.error('Could not load page data: ' + e.message); }
    setLoading(false);
  }

  // Auto-calculate days when dates change
  function handleDateChange(field, value) {
    const updated = { ...form, [field]: value };
    if (updated.start_date && updated.end_date) {
      const start = new Date(updated.start_date);
      const end   = new Date(updated.end_date);
      const days  = Math.max(0, Math.round((end - start) / (1000 * 60 * 60 * 24)) + 1);
      updated.num_days = days;
    }
    setForm(updated);
  }

  async function save() {
    if (!form.student_name.trim()) { toast.error('Enter student name'); return; }
    if (form.start_date && form.end_date && form.end_date < form.start_date) { toast.error('End date cannot be before the start date'); setFormTab('internship'); return; }
    if (form.add_to_accounts === 'Y' && !(Number(form.paid_amount) > 0 && form.paid_date)) { toast.error('Enter the paid amount and paid date to add this payment to Accounts'); setFormTab('payment'); return; }
    // Same phone number already has an internship record?
    if (!editId && form.contact_number) {
      const ph = String(form.contact_number).replace(/\D/g, '').slice(-10);
      const dup = ph.length >= 10 && internships.find(i => String(i.contact_number || '').replace(/\D/g, '').slice(-10) === ph);
      if (dup && !window.confirm(`A record with this phone number already exists: ${dup.student_name} (${dup.college_name || 'no college'}). Add another one?`)) return;
    }
    setSaving(true);
    const payload = {
      student_name:              form.student_name.trim(),
      guardian_name:             form.guardian_name || null,
      contact_number:            form.contact_number || null,
      whatsapp_number:           form.whatsapp_number || null,
      college_id:                form.college_id || null,
      college_name:              colleges.find(c => c.id === form.college_id)?.college_name || form.college_name_text || null,
      semester:                  form.semester || null,
      passed_out_year:           form.passed_out_year || null,
      passedout_year:            form.passed_out_year || null,
      stream:                    form.stream || null,
      centre:                    form.centre || null,
      start_date:                form.start_date || null,
      end_date:                  form.end_date || null,
      num_days:                  form.num_days ? Number(form.num_days) : null,
      topics_covered:            form.topics_covered || null,
      trainer:                   form.trainer || null,
      demo_provided:             form.demo_provided || null,
      demo_provided_by:          form.demo_provided_by || null,
      hostel_provided:           form.hostel_provided || null,
      certificate_start_date:    form.certificate_start_date || null,
      certificate_end_date:      form.certificate_end_date || null,
      certificate_duration:      form.certificate_duration || null,
      certificate_collected_date: form.certificate_collected_date || null,
      collected_person_name:     form.collected_person_name || null,
      certificate_issuer:        form.certificate_issuer || null,
      admission_taken:           form.admission_taken || null,
      batch_id:                  form.batch_id || null,
      batch_name:                batches.find(b => b.id === form.batch_id)?.batch_name || null,
      cert_issued_date:          form.cert_issued_date || null,
      cert_issued_by:            form.cert_issued_by || null,
      remarks:                   form.remarks || null,
    };

    const { data: saved, error } = editId
      ? await supabase.from('internships').update(payload).eq('id', editId).select('id').single()
      : await supabase.from('internships').insert(payload).select('id').single();

    if (error) { toast.error('Error: ' + error.message); setSaving(false); return; }

    // Auto-sync to Accounts if selected (never twice for the same payment)
    if (form.add_to_accounts === 'Y' && Number(form.paid_amount) > 0 && form.paid_date) {
      const reference = form.receipt_no || `INT-${saved?.id || editId}`;
      const { data: already } = await supabase.from('accounts_transactions').select('id')
        .eq('source', 'internship').eq('reference', reference).eq('amount', Number(form.paid_amount)).eq('date', form.paid_date).limit(1);
      if (already && already.length) {
        toast.success((editId ? 'Updated' : 'Saved') + ' — this payment was already in Accounts, not added again');
      } else {
        const { error: accErr } = await supabase.from('accounts_transactions').insert({
          company: 'ARMSTECH',
          date: form.paid_date,
          type: 'Cash In',
          category: form.payment_category || 'Tuition Fees',
          payment_mode: form.payment_mode || 'Cash',
          amount: Number(form.paid_amount),
          reference,
          description: `Internship Payment — ${form.student_name || ''}${form.payment_type ? ' (' + form.payment_type + ')' : ''}`,
          source: 'internship',
        });
        if (accErr) toast.error('Internship saved, but the payment was NOT added to Accounts: ' + accErr.message, { duration: 9000 });
        else toast.success(editId ? 'Updated ✅ + synced to Accounts' : 'Saved ✅ + synced to Accounts');
      }
    } else {
      toast.success(editId ? 'Updated!' : 'Internship record created!');
    }

    setShowModal(false);
    setEditId(null);
    setForm(emptyForm);
    setFormTab('personal');
    setSaving(false);
    loadInternships();
  }

  function openEdit(item) {
    setEditId(item.id);
    const matchedCollege = colleges.find(c => c.college_name === item.college_name || c.id === item.college_id);
    setForm({
      ...emptyForm,
      student_name:              item.student_name || '',
      guardian_name:             item.guardian_name || '',
      contact_number:            item.contact_number || '',
      whatsapp_number:           item.whatsapp_number || '',
      college_id:                matchedCollege?.id || item.college_id || '',
      college_name_text:         item.college_name || matchedCollege?.college_name || '',
      semester:                  item.semester || '',
      passed_out_year:           item.passed_out_year || item.passedout_year || '',
      stream:                    item.stream || '',
      centre:                    item.centre || '',
      start_date:                item.start_date || '',
      end_date:                  item.end_date || '',
      num_days:                  item.num_days || (item.start_date && item.end_date
        ? String(Math.max(0, Math.round((new Date(item.end_date) - new Date(item.start_date)) / (1000*60*60*24)) + 1))
        : ''),
      topics_covered:            item.topics_covered || '',
      trainer:                   item.trainer || item.trainer_name || '',
      demo_provided:             item.demo_provided || '',
      demo_provided_by:          item.demo_provided_by || '',
      hostel_provided:           item.hostel_provided || '',
      certificate_start_date:    item.certificate_start_date || '',
      certificate_end_date:      item.certificate_end_date || '',
      certificate_duration:      item.certificate_duration || '',
      certificate_collected_date: item.certificate_collected_date || '',
      collected_person_name:     item.collected_person_name || '',
      certificate_issuer:        item.certificate_issuer || '',
      admission_taken:           item.admission_taken || '',
      cert_issued_date:          item.cert_issued_date || '',
      cert_issued_by:            item.cert_issued_by || '',
      batch_id:                  item.batch_id || '',
      remarks:                   item.remarks || '',
    });
    setFormTab('personal');
    setShowModal(true);
  }

  async function deleteItem(id, name) {
    if (!window.confirm(`Delete internship record for "${name}"?`)) return;
    const { error } = await supabase.from('internships').delete().eq('id', id);
    if (error) toast.error(error.message);
    else { toast.success('Deleted'); setSelectedItems(prev => { const n = new Set(prev); n.delete(id); return n; }); loadInternships(); }
  }

  const uniqueStreams   = [...new Set(internships.map(i => normStream(i.stream)).filter(Boolean))].sort();
  const uniqueSemesters = [...new Set(internships.map(i => i.semester).filter(Boolean))].sort((a,b)=>isNaN(a)?1:isNaN(b)?-1:Number(a)-Number(b));
  const uniqueColleges = [...new Set(internships.map(i => i.colleges?.college_name || i.college_name).filter(Boolean))].sort();
  const uniquePassedout = [...new Set(internships.map(i => i.passedout_year || i.passed_out_year).filter(Boolean).map(String))].sort();
  const uniqueTrainers  = [...new Set(internships.map(i => i.trainer_name || i.trainer).filter(Boolean))].sort();
  const uniqueCentres   = [...new Set(internships.map(i => normCentre(i.centre)).filter(Boolean))].sort();

  const filtered = internships.filter(i => {
    if (filterReport === 'EKM' && normCentre(i.centre) !== 'EKM') return false;
    if (filterReport === 'TVM' && normCentre(i.centre) !== 'TVM') return false;
    if (filterReport === 'cert_issued' && !isCertIssued(i)) return false;
    if (filterReport === 'cert_pending' && isCertIssued(i)) return false;
    if (filterReport === 'admission' && i.admission_taken!=='yes') return false;
    if (filterReport && !['EKM','TVM','cert_issued','cert_pending','admission','all',''].includes(filterReport) && normStream(i.stream).toLowerCase()!==filterReport.toLowerCase()) return false;
    const q = search.trim().toLowerCase();
    const matchSearch   = !q || i.student_name?.toLowerCase().includes(q) || (i.colleges?.college_name || i.college_name)?.toLowerCase().includes(q) || i.stream?.toLowerCase().includes(q) || String(i.contact_number||'').replace(/\s/g,'').includes(q.replace(/\s/g,''));
    const matchCollege  = !filterCollege  || (i.colleges?.college_name || i.college_name) === filterCollege;
    const matchCentre   = !filterCentre   || normCentre(i.centre) === filterCentre;
    const matchSemester = !filterSemester || i.semester === filterSemester;
    const matchStream   = !filterStream   || normStream(i.stream).toLowerCase() === filterStream.toLowerCase();
    const matchPassedout = !filterPassedout || String(i.passedout_year || i.passed_out_year || '') === filterPassedout;
    const matchTrainer  = !filterTrainer  || (i.trainer_name || i.trainer) === filterTrainer;
    return matchSearch && matchCollege && matchCentre && matchSemester && matchStream && matchPassedout && matchTrainer;
  });

  function exportExcel() {
    if (!filtered.length) { toast.error('Nothing to export'); return; }
    const rows = filtered.map((i, n) => ({
      'S.No': n + 1, 'Student': i.student_name || '', 'Guardian': i.guardian_name || '', 'Contact': i.contact_number || '', 'WhatsApp': i.whatsapp_number || '',
      'College': i.colleges?.college_name || i.college_name || '', 'Stream': normStream(i.stream), 'Semester': i.semester || '', 'Passed Out': i.passedout_year || i.passed_out_year || '',
      'Centre': normCentre(i.centre), 'Start': i.start_date || '', 'End': i.end_date || '', 'Days': i.num_days || '', 'Trainer': i.trainer_name || i.trainer || '',
      'Topics': i.topics_covered || '', 'Demo': i.demo_provided || '', 'Hostel': i.hostel_provided || '', 'Admission': i.admission_taken || '', 'Batch': i.batch_name || '',
      'Cert Issued Date': i.cert_issued_date || i.certificate_collected_date || '', 'Cert Issued By': i.cert_issued_by || i.certificate_issuer || '', 'Remarks': i.remarks || '',
    }));
    saveStyledXlsx(`Internships_${new Date().toLocaleDateString('en-CA')}.xlsx`, [{ name: 'Internships', title: 'Internship Report — Armstech Engineers', rows }]);
  }

  async function bulkSetAdmission(status) {
    const ids = [...selectedItems];
    if (!ids.length || !status) return;
    if (!window.confirm(`Update ${ids.length} records to "${status}"?`)) return;
    let failed = 0;
    for (let i = 0; i < ids.length; i += 100) {
      const { error } = await supabase.from('internships').update({ admission_taken: status }).in('id', ids.slice(i, i + 100));
      if (error) { failed += ids.slice(i, i + 100).length; toast.error(error.message); }
    }
    if (failed < ids.length) toast.success(`${ids.length - failed} records updated`);
    setSelectedItems(new Set());
    loadInternships();
  }

  return (
    <div className="page">
      <div className="page-header">
        <div><h1 className="page-title">Internship</h1><p className="page-subtitle">Manage internship students and their details</p></div>
        <div style={{display:"flex",gap:8,flexWrap:'wrap',alignItems:'center'}}>
          <div style={{display:'flex',gap:4}}>
            <button className={`btn btn-sm ${mainTab==='internship'?'btn-primary':'btn-ghost'}`} onClick={()=>setMainTab('internship')}>🎓 Internship</button>
            <button className={`btn btn-sm ${mainTab==='visit'?'btn-primary':'btn-ghost'}`} onClick={()=>setMainTab('visit')}>🏭 Industrial Visit</button>
          </div>
          {mainTab==='internship'&&<>
            <button className={`btn btn-sm ${showReport?'btn-primary':'btn-ghost'}`} onClick={()=>setShowReport(r=>!r)}>📊 Report</button>
            {selectedItems.size>0&&canEdit('internship')&&(
              <select className="btn btn-sm btn-ghost" value="" onChange={e=>bulkSetAdmission(e.target.value)}>
                <option value="">Bulk Status ({selectedItems.size})...</option>
                <option value="yes">Admission Taken</option>
                <option value="no">No Admission</option>
                <option value="pending">Pending</option>
              </select>
            )}
            <button className="btn btn-sm btn-ghost" onClick={exportExcel}>⬇ Export</button>
            <ImportButton configKey="internships" onImported={()=>loadAll()}/>
            {canEdit('internship') && <button className="btn btn-primary" onClick={()=>{setEditId(null);setForm(emptyForm);setFormTab('personal');setShowModal(true);}}><Plus size={14}/> New Internship</button>}
          </>}
          {mainTab==='visit'&&canEdit('internship')&&(
            <button className="btn btn-primary" onClick={()=>{setEditVisitId(null);setVisitForm(emptyVisitForm);setVisitStudents([{ ...emptyVisitStudent }]);setShowVisitModal(true);}}><Plus size={14}/> New Visit</button>
          )}
        </div>
      </div>

      {/* Report */}
      {mainTab==='internship' && showReport && (
        <div className="card" style={{marginBottom:16}}>
          <div style={{fontWeight:700,fontSize:14,marginBottom:12}}>📊 Internship Report</div>
          {(()=>{
            const total = internships.length;
            const ekm = internships.filter(i=>normCentre(i.centre)==='EKM').length;
            const tvm = internships.filter(i=>normCentre(i.centre)==='TVM').length;
            const certIssued = internships.filter(isCertIssued).length;
            const certPending = total - certIssued;
            const streams = [...new Set(internships.map(i=>normStream(i.stream)).filter(Boolean))].sort();
            const summaryCards = [
              {label:'Total',value:total,color:'var(--accent)',filter:'all'},
              {label:'EKM',value:ekm,color:'var(--blue)',filter:'EKM'},
              {label:'TVM',value:tvm,color:'var(--purple)',filter:'TVM'},
              {label:'Cert Issued',value:certIssued,color:'var(--green)',filter:'cert_issued'},
              {label:'Cert Pending',value:certPending,color:'var(--amber)',filter:'cert_pending'},
              {label:'Admission Taken',value:internships.filter(i=>i.admission_taken==='yes').length,color:'var(--cyan)',filter:'admission'},
            ];
            return (
              <>
                <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(120px,1fr))',gap:10,marginBottom:16}}>
                  {summaryCards.map(s=>(
                    <div key={s.label} className="card" style={{textAlign:'center',padding:10,borderLeft:`4px solid ${s.color}`,cursor:s.filter?'pointer':'default'}}
                      onClick={()=>s.filter&&setFilterReport(filterReport===s.filter?'':s.filter)}>
                      <div style={{fontSize:22,fontWeight:800,color:s.color}}>{s.value}</div>
                      <div style={{fontSize:11,color:'var(--text3)'}}>{s.label}</div>
                      {s.filter&&filterReport===s.filter&&<div style={{fontSize:9,color:s.color}}>▼ filtering</div>}
                    </div>
                  ))}
                </div>
                {streams.length>0&&(
                  <>
                    <div style={{fontWeight:600,fontSize:13,marginBottom:8}}>By Stream</div>
                    <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))',gap:8,marginBottom:16}}>
                      {streams.map(s=>{
                        const count=internships.filter(i=>normStream(i.stream)===s).length;
                        const cert=internships.filter(i=>normStream(i.stream)===s&&isCertIssued(i)).length;
                        return (
                          <div key={s} className="card" style={{padding:'8px 12px',cursor:'pointer',border:filterReport===s?'2px solid var(--accent)':'1px solid var(--border)'}}
                            onClick={()=>setFilterReport(filterReport===s?'':s)}>
                            <div style={{fontWeight:600,fontSize:12}}>{s}</div>
                            <div style={{fontSize:11,color:'var(--text3)',marginTop:2}}>
                              {count} students · <span style={{color:'var(--green)'}}>{cert} cert issued</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16}}>
                  {[['EKM',ekm,'var(--blue)'],['TVM',tvm,'var(--purple)']].map(([centre,count,color])=>{
                    return count>0?(
                      <div key={centre} className="card" style={{padding:12,borderLeft:`4px solid ${color}`}}>
                        <div style={{fontWeight:700,fontSize:13,marginBottom:6,color}}>{centre} — {count} students</div>
                        <div style={{fontSize:11,color:'var(--text3)'}}>
                          Cert Issued: {internships.filter(i=>normCentre(i.centre)===centre&&isCertIssued(i)).length}
                        </div>
                      </div>
                    ):null;
                  })}
                </div>
              </>
            );
          })()}
        </div>
      )}

      {/* Industrial Visit Tab */}
      {mainTab==='visit' && (
        <div>
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))',gap:16,marginBottom:16}}>
            {visits.map(v=>(
              <div key={v.id} className="card" style={{cursor:'pointer'}} onClick={()=>setViewVisit(v)}>
                <div style={{fontWeight:700,fontSize:14,marginBottom:4}}>{v.college_name}{v.centre && <span className="badge badge-cyan" style={{marginLeft:6,fontSize:9}}>{normCentre(v.centre)}</span>}</div>
                <div style={{fontSize:12,color:'var(--text3)',marginBottom:4}}>
                  📅 {fmtDMY(v.visit_date)} · 👥 {v.industrial_visit_students?.length||0} students
                </div>
                <div style={{fontSize:11,color:'var(--text3)'}}>
                  {v.stream} {v.semester?`· Sem ${v.semester}`:''} {v.passout_year?`· ${v.passout_year}`:''}
                </div>
                {v.fees_collected>0&&<div style={{fontSize:11,color:'var(--green)',marginTop:4}}>₹{Number(v.fees_collected).toLocaleString('en-IN')} collected</div>}
                {canEdit('internship')&&(
                  <div style={{display:'flex',gap:4,marginTop:8}} onClick={e=>e.stopPropagation()}>
                    <button className="btn btn-ghost btn-sm" style={{fontSize:11}} onClick={()=>{
                      setEditVisitId(v.id);
                      setVisitForm({college_name:v.college_name,college_id:v.college_id||'',centre:v.centre||'',visit_date:v.visit_date||'',enquiry_date:v.enquiry_date||'',stream:v.stream||'',semester:v.semester||'',passout_year:v.passout_year||'',fees_collected:v.fees_collected||'',faculty_taken_class:v.faculty_taken_class||'',remarks:v.remarks||'',coordinator_id:v.coordinator_id||''});
                      setVisitStudents(v.industrial_visit_students?.length>0?v.industrial_visit_students.map(x=>({student_name:x.student_name||'',contact_number:x.contact_number||'',stream:x.stream||'',semester:x.semester||'',passout_year:x.passout_year||''})):[{ ...emptyVisitStudent }]);
                      setShowVisitModal(true);
                    }}>✏️ Edit</button>
                    <button className="btn btn-ghost btn-sm" style={{fontSize:11,color:'var(--red)'}} onClick={()=>deleteVisit(v)}>🗑 Delete</button>
                  </div>
                )}
              </div>
            ))}
            {visits.length===0&&<div style={{gridColumn:'1/-1',textAlign:'center',padding:32,color:'var(--text3)'}}>No industrial visits recorded yet</div>}
          </div>
        </div>
      )}

      {/* Stats (Internship tab only) */}
      {mainTab==='internship' && <div className="stats-grid" style={{ marginBottom: 20 }}>
        {[
          { label: 'Total',       value: internships.length,                                         color: 'var(--accent)' },
          { label: 'Admission Taken', value: internships.filter(i => i.admission_taken === 'yes').length, color: 'var(--green)' },
          { label: 'Demo Given',  value: internships.filter(i => i.demo_provided === 'yes').length,  color: 'var(--cyan)' },
          { label: 'Hostel Provided', value: internships.filter(i => i.hostel_provided === 'yes').length, color: 'var(--amber)' },
        ].map(s => (
          <div key={s.label} className="stat-card">
            <div className="stat-label">{s.label}</div>
            <div className="stat-value" style={{ color: s.color, fontSize: 24 }}>{s.value}</div>
          </div>
        ))}
      </div>}

      {mainTab==='internship' && <><div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <div className="search-bar"><Search /><input placeholder="Search student, college, phone..." value={search} onChange={e => setSearch(e.target.value)} /></div>
        <SearchableSelect options={uniqueColleges.map(n => ({ value: n, label: n }))} value={filterCollege} onChange={v => setFilterCollege(v)} placeholder="College" searchPlaceholder="Search college..." style={{ minWidth: 150, maxWidth: 200 }} />
        <select style={{ width: 100 }} value={filterCentre} onChange={e => setFilterCentre(e.target.value)}>
          <option value="">Centre</option>
          {uniqueCentres.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select style={{ width: 110 }} value={filterSemester} onChange={e => setFilterSemester(e.target.value)}>
          <option value="">Semester</option>
          {uniqueSemesters.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select style={{ width: 110 }} value={filterPassedout} onChange={e => setFilterPassedout(e.target.value)}>
          <option value="">Passed Out Yr</option>
          {uniquePassedout.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        <select style={{ width: 110 }} value={filterStream} onChange={e => setFilterStream(e.target.value)}>
          <option value="">Stream</option>
          {uniqueStreams.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select style={{ width: 120 }} value={filterTrainer} onChange={e => setFilterTrainer(e.target.value)}>
          <option value="">Trainer</option>
          {uniqueTrainers.map(t => <option key={t} value={t}>{t}</option>)}
        </select>

        <span style={{fontSize:12,color:'var(--text3)',padding:'4px 8px'}}>
          {filtered.length}/{internships.length} students
        </span>
        {(search || filterCollege || filterCentre || filterSemester || filterPassedout || filterStream || filterTrainer || filterReport) && (
          <button className="btn btn-ghost btn-sm" onClick={() => { setSearch(''); setFilterCollege(''); setFilterCentre(''); setFilterSemester(''); setFilterPassedout(''); setFilterStream(''); setFilterTrainer(''); setFilterReport(''); }}>✕ Clear</button>
        )}
      </div></>
      }
      {mainTab==='internship' && loading && <div className="loading"><div className="spinner" /></div>}
      {mainTab==='internship' && !loading && (
        <div className="table-wrap">
          <table style={{minWidth:1200}}>
            <thead>
              <tr>
                <th style={{width:36}}><input type="checkbox"
                  checked={selectedItems.size===filtered.length&&filtered.length>0}
                  onChange={e=>setSelectedItems(e.target.checked?new Set(filtered.map(i=>i.id)):new Set())}
                /></th>
                <th style={{minWidth:30}}>#</th>
                <th style={{minWidth:140}}>Student Name</th>
                <th style={{minWidth:130}}>Contact</th>
                <th style={{minWidth:150}}>College</th>
                <th>Centre</th>
                <th>Start</th>
                <th>End</th>
                <th>Demo</th>
                <th>Hostel</th>
                <th>Admission</th>
                <th>Certificate</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={13}><div className="empty-state"><GraduationCap /><h3>No internship records yet</h3><p>Click "New Internship" to add one</p></div></td></tr>
              ) : filtered.map((item, i) => (
                <tr key={item.id}>
                  <td><input type="checkbox" checked={selectedItems.has(item.id)} onChange={()=>toggleSel(item.id)}/></td>
                  <td style={{ color: 'var(--text3)', fontSize: 12 }}>{i + 1}</td>
                  <td>
                    <div style={{ fontWeight: 500 }}>{item.student_name}</div>
                    <div style={{ fontSize: 11, color: 'var(--text3)' }}>{item.stream || '—'}</div>
                  </td>
                  <td style={{fontSize:12}}><div style={{display:'flex',alignItems:'center',gap:4}}><span style={{fontFamily:'monospace'}}>{item.contact_number||'—'}</span>{item.contact_number&&<CallBtn phone={item.contact_number}/>}{item.whatsapp_number&&<WABtn phone={item.whatsapp_number}/>}</div></td>
                  <td style={{ fontSize: 12 }}>{item.college_name || colleges.find(c => c.id === item.college_id)?.college_name || '—'}</td>
                  <td><span className={`badge ${normCentre(item.centre) === 'TVM' ? 'badge-purple' : 'badge-cyan'}`}>{normCentre(item.centre) || '—'}</span></td>
                  <td style={{ fontSize: 12 }}>{fmtDMY(item.start_date) || '—'}</td>
                  <td style={{ fontSize: 12 }}>{fmtDMY(item.end_date) || '—'}</td>
                  <td><span className={`badge ${item.demo_provided === 'yes' ? 'badge-green' : 'badge-gray'}`}>{item.demo_provided === 'yes' ? 'Yes' : item.demo_provided === 'no' ? 'No' : '—'}</span></td>
                  <td><span className={`badge ${item.hostel_provided === 'yes' ? 'badge-green' : 'badge-gray'}`}>{item.hostel_provided === 'yes' ? 'Yes' : item.hostel_provided === 'no' ? 'No' : '—'}</span></td>
                  <td><span className={`badge ${item.admission_taken === 'yes' ? 'badge-green' : item.admission_taken === 'pending' ? 'badge-amber' : 'badge-gray'}`}>{item.admission_taken === 'yes' ? 'Taken' : item.admission_taken === 'no' ? 'No' : item.admission_taken === 'pending' ? 'Pending' : '—'}</span></td>
                  <td><span className={`badge ${isCertIssued(item) ? 'badge-green' : 'badge-gray'}`}>{isCertIssued(item) ? 'Issued' : 'Pending'}</span></td>
                  <td>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button className="btn btn-ghost btn-sm" title="View" onClick={() => setViewItem(item)}><Eye size={13} /></button>
                      {canEdit('internship') && <button className="btn btn-ghost btn-sm" title="Edit" onClick={() => openEdit(item)}><Edit2 size={13} /></button>}
                      {canEdit('internship') && <button className="btn btn-ghost btn-sm" style={{ color: 'var(--red)' }} title="Delete" onClick={() => deleteItem(item.id, item.student_name)}><Trash2 size={13} /></button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── VIEW MODAL ── */}
      {viewItem && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setViewItem(null)}>
          <div className="modal modal-lg">
            <div className="modal-header">
              <span className="modal-title">{viewItem.student_name}</span>
              <button className="btn btn-ghost btn-sm" onClick={() => setViewItem(null)}><X size={16} /></button>
            </div>
            <div className="modal-body">
              <div className="form-section-heading">Personal Details</div>
              {[
                ['Student Name', viewItem.student_name],
                ['Guardian', viewItem.guardian_name],
                ['Contact', viewItem.contact_number],
                ['WhatsApp', viewItem.whatsapp_number],
                ['College', viewItem.colleges?.college_name || viewItem.college_name],
                ['Semester', viewItem.semester],
                ['Passed Out Year', viewItem.passed_out_year || viewItem.passedout_year],
                ['Stream', viewItem.stream],
              ].map(([l, v]) => v ? <div key={l} className="detail-row"><div className="detail-label">{l}</div><div className="detail-value">{v}</div></div> : null)}

              <div className="form-section-heading" style={{ marginTop: 16 }}>Internship Details</div>
              {[
                ['Centre', viewItem.centre],
                ['Start Date', viewItem.start_date],
                ['End Date', viewItem.end_date],
                ['Number of Days', viewItem.num_days],
                ['Topics Covered', viewItem.topics_covered],
                ['Trainer', viewItem.trainer],
                ['Demo Provided', viewItem.demo_provided],
                ['Demo Provided By', viewItem.demo_provided_by],
                ['Hostel Provided', viewItem.hostel_provided],
                ['Certificate Start', viewItem.certificate_start_date],
                ['Certificate End', viewItem.certificate_end_date],
                ['Certificate Duration', viewItem.certificate_duration],
                ['Certificate Issued Date', viewItem.cert_issued_date],
                ['Certificate Issued By', viewItem.cert_issued_by],
                ['Certificate Collected Date', viewItem.certificate_collected_date],
                ['Collected Person Name', viewItem.collected_person_name],
                ['Certificate Issuer', viewItem.certificate_issuer],
              ].map(([l, v]) => v ? <div key={l} className="detail-row"><div className="detail-label">{l}</div><div className="detail-value">{v}</div></div> : null)}

              <div className="form-section-heading" style={{ marginTop: 16 }}>Admission</div>
              {[
                ['Admission Taken', viewItem.admission_taken],
                ['Batch', viewItem.batches?.batch_name || viewItem.batch_name],
                ['Remarks', viewItem.remarks],
              ].map(([l, v]) => v ? <div key={l} className="detail-row"><div className="detail-label">{l}</div><div className="detail-value">{v}</div></div> : null)}
            </div>
            <div className="modal-footer">
              {canEdit('internship') && <button className="btn btn-secondary" onClick={() => { setViewItem(null); openEdit(viewItem); }}><Edit2 size={13} /> Edit</button>}
              <button className="btn btn-primary" onClick={() => setViewItem(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* ── ADD / EDIT MODAL ── */}
      {showModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal modal-lg">
            <div className="modal-header">
              <span className="modal-title">{editId ? 'Edit Internship' : 'New Internship'}</span>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowModal(false)}><X size={16} /></button>
            </div>

            {/* Form tabs */}
            <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', padding: '0 24px' }}>
              {[['personal','👤 Personal'],['internship','🏢 Internship'],['admission','📋 Admission'],['payment','💳 Payment']].map(([v, l]) => (
                <button key={v} onClick={() => setFormTab(v)} style={{ padding: '10px 16px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 13, fontWeight: 500, color: formTab === v ? 'var(--accent)' : 'var(--text2)', borderBottom: formTab === v ? '2px solid var(--accent)' : '2px solid transparent', fontFamily: 'var(--font-body)' }}>
                  {l}
                </button>
              ))}
            </div>

            <div className="modal-body">

              {/* ── PERSONAL DETAILS TAB ── */}
              {formTab === 'payment' && (
            <div>
              <div className="form-section-heading" style={{marginTop:0}}>Payment Details</div>
              <div className="form-grid">
                <div className="form-group"><label>Paid Amount (₹)</label>
                  <input type="number" min="0" value={form.paid_amount} onChange={e=>setForm(f=>({...f,paid_amount:e.target.value}))} placeholder="Amount received"/>
                </div>
                <div className="form-group"><label>Paid Date</label>
                  <DateInput value={form.paid_date} onChange={e=>setForm(f=>({...f,paid_date:e.target.value}))}/>
                </div>
                <div className="form-group"><label>Payment Type</label>
                  <select value={form.payment_type} onChange={e=>setForm(f=>({...f,payment_type:e.target.value}))}>
                    {PAYMENT_TYPES_LIST.map(t=><option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div className="form-group"><label>Payment Mode</label>
                  <select value={form.payment_mode} onChange={e=>setForm(f=>({...f,payment_mode:e.target.value}))}>
                    <option value="">Select Account</option>
                    {PAYMENT_MODES_LIST.map(m=><option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
                <div className="form-group"><label>Category</label>
                  <select value={form.payment_category || 'Tuition Fees'} onChange={e=>setForm(f=>({...f,payment_category:e.target.value}))}>
                    {PAYMENT_CATS_LIST.map(c=><option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="form-group"><label>Receipt / Reference No</label>
                  <input value={form.receipt_no} onChange={e=>setForm(f=>({...f,receipt_no:e.target.value}))} placeholder="Receipt or reference number"/>
                </div>
                <div className="form-group"><label>Add to Accounts?</label>
                  <select value={form.add_to_accounts} onChange={e=>setForm(f=>({...f,add_to_accounts:e.target.value}))} style={{borderColor:form.add_to_accounts==='Y'?'var(--green)':'var(--border)',fontWeight:600}}>
                    <option value="N">N — No</option>
                    <option value="Y">Y — Yes (auto-sync)</option>
                  </select>
                </div>
              </div>
              {form.add_to_accounts==='Y' && (
                <div style={{marginTop:10,padding:'8px 12px',background:'#f0fdf4',border:'1px solid #86efac',borderRadius:8,fontSize:11,color:'#15803d'}}>
                  💡 When saved, this payment will automatically appear in <strong>Accounts → ARMSTECH → Cash In</strong> (once — saving again will not duplicate it)
                </div>
              )}
            </div>
          )}
          {formTab === 'personal' && (
                <div className="form-grid">
                  <div className="form-group full">
                    <label>Student Name *</label>
                    <input value={form.student_name} onChange={e => setForm(f => ({ ...f, student_name: e.target.value }))} placeholder="Full name" />
                  </div>
                  <div className="form-group">
                    <label>Guardian Name</label>
                    <input value={form.guardian_name || ''} onChange={e => setForm(f => ({ ...f, guardian_name: e.target.value }))} placeholder="Parent / Guardian name" />
                  </div>
                  <div className="form-group">
                    <label>Contact Number</label>
                    <input value={form.contact_number} onChange={e => setForm(f => ({ ...f, contact_number: e.target.value }))} placeholder="Mobile number" />
                  </div>
                  <div className="form-group">
                    <label>WhatsApp Number</label>
                    <input value={form.whatsapp_number} onChange={e => setForm(f => ({ ...f, whatsapp_number: e.target.value }))} placeholder="WhatsApp number" />
                  </div>
                  <div className="form-group">
                    <label>College</label>
                    <select value={form.college_id} onChange={e => setForm(f => ({ ...f, college_id: e.target.value }))}>
                      <option value="">Select College</option>
                      {colleges.map(c => <option key={c.id} value={c.id}>{c.college_name}</option>)}
                    </select>
                    {/* Show text if college not in master list */}
                    {!colleges.find(c => c.id === form.college_id) && form.college_id === '' && (
                      <input type="text" value={form.college_name_text || ''} onChange={e=>setForm(f=>({...f,college_name_text:e.target.value}))} placeholder="Or type college name directly..." style={{marginTop:4}}/>
                    )}
                  </div>
                  <div className="form-group">
                    <label>Semester</label>
                    <select value={form.semester} onChange={e => setForm(f => ({ ...f, semester: e.target.value }))}>
                      <option value="">Select Semester</option>
                      {SEMESTERS.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Passed Out Year</label>
                    <input value={form.passed_out_year} onChange={e => setForm(f => ({ ...f, passed_out_year: e.target.value }))} placeholder="e.g. 2024" type="number" min="2000" max="2099" />
                  </div>
                  <div className="form-group">
                    <label>Stream</label>
                    <select value={form.stream} onChange={e => setForm(f => ({ ...f, stream: e.target.value }))}>
                      <option value="">Select Stream</option>
                      {STREAMS.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>
              )}

              {/* ── INTERNSHIP DETAILS TAB ── */}
              {formTab === 'internship' && (
                <div className="form-grid">
                  <div className="form-group">
                    <label>Centre</label>
                    <select value={form.centre} onChange={e => setForm(f => ({ ...f, centre: e.target.value }))}>
                      <option value="">Select Centre</option>
                      {form.centre && !CENTRES.includes(form.centre) && <option value={form.centre}>{form.centre}</option>}
                      {CENTRES.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Start Date</label>
                    <DateInput value={form.start_date} onChange={e => handleDateChange('start_date', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label>End Date</label>
                    <DateInput value={form.end_date} onChange={e => handleDateChange('end_date', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label>How Many Days</label>
                    <input type="number" value={form.num_days} readOnly placeholder="Auto-calculated" style={{background:'var(--accent-light)',fontWeight:600,color:'var(--accent)',cursor:'default'}} />
                  </div>
                  <div className="form-group full">
                    <label>Topics Covered</label>
                    <textarea value={form.topics_covered} onChange={e => setForm(f => ({ ...f, topics_covered: e.target.value }))} rows={2} placeholder="List of topics covered" />
                  </div>
                  <div className="form-group">
                    <label>Trainer / Faculty</label>
                    <input type="text" value={form.trainer||''} placeholder="Enter trainer name(s)" onChange={e => setForm(f => ({ ...f, trainer: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label>Demo Provided</label>
                    <select value={form.demo_provided} onChange={e => setForm(f => ({ ...f, demo_provided: e.target.value }))}>
                      <option value="">Select</option>
                      <option value="yes">Yes</option>
                      <option value="no">No</option>
                    </select>
                  </div>
                  {form.demo_provided === 'yes' && (
                    <div className="form-group">
                      <label>Demo Provided By</label>
                      <input value={form.demo_provided_by} onChange={e => setForm(f => ({ ...f, demo_provided_by: e.target.value }))} placeholder="Name of person" />
                    </div>
                  )}
                  <div className="form-group">
                    <label>Hostel Provided</label>
                    <select value={form.hostel_provided} onChange={e => setForm(f => ({ ...f, hostel_provided: e.target.value }))}>
                      <option value="">Select</option>
                      <option value="yes">Yes</option>
                      <option value="no">No</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Certificate Start Date</label>
                    <DateInput value={form.certificate_start_date || ''} onChange={e => setForm(f => ({ ...f, certificate_start_date: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label>Certificate End Date</label>
                    <DateInput value={form.certificate_end_date || ''} onChange={e => setForm(f => ({ ...f, certificate_end_date: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label>Certificate Duration Given</label>
                    <input value={form.certificate_duration} onChange={e => setForm(f => ({ ...f, certificate_duration: e.target.value }))} placeholder="e.g. 30 days" />
                  </div>
                  <div className="form-group">
                    <label>Certificate Collected Date</label>
                    <DateInput value={form.certificate_collected_date} onChange={e => setForm(f => ({ ...f, certificate_collected_date: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label>Collected Person Name</label>
                    <input value={form.collected_person_name} onChange={e => setForm(f => ({ ...f, collected_person_name: e.target.value }))} placeholder="Name of person collecting" />
                  </div>
                  <div className="form-group">
                    <label>Issuer</label>
                    <input value={form.certificate_issuer} onChange={e => setForm(f => ({ ...f, certificate_issuer: e.target.value }))} placeholder="Issuing authority" />
                  </div>
                  <div className="form-group">
                    <label>Certificate Issued Date</label>
                    <DateInput value={form.cert_issued_date || ''} onChange={e => setForm(f => ({ ...f, cert_issued_date: e.target.value }))} />
                  </div>
                  <div className="form-group">
                    <label>Certificate Issued By</label>
                    <input value={form.cert_issued_by || ''} onChange={e => setForm(f => ({ ...f, cert_issued_by: e.target.value }))} placeholder="Staff who handed over the certificate" />
                  </div>
                </div>
              )}

              {/* ── ADMISSION TAB ── */}
              {formTab === 'admission' && (
                <div className="form-grid">
                  <div className="form-group">
                    <label>Admission Taken</label>
                    <select value={form.admission_taken} onChange={e => setForm(f => ({ ...f, admission_taken: e.target.value }))}>
                      <option value="">Select</option>
                      <option value="yes">Yes</option>
                      <option value="no">No</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Which Batch</label>
                    <select value={form.batch_id} onChange={e => setForm(f => ({ ...f, batch_id: e.target.value }))}>
                      <option value="">Select Batch</option>
                      {batches.map(b => <option key={b.id} value={b.id}>{b.batch_name} ({b.batch_code})</option>)}
                    </select>
                  </div>
                  <div className="form-group full">
                    <label>Remarks</label>
                    <textarea value={form.remarks} onChange={e => setForm(f => ({ ...f, remarks: e.target.value }))} rows={3} placeholder="Any additional notes..." />
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer">
              {/* Tab navigation buttons */}
              <div style={{ flex: 1, display: 'flex', gap: 8 }}>
                {formTab !== 'personal'   && <button className="btn btn-secondary" onClick={() => setFormTab(formTab==='admission'?'internship':formTab==='payment'?'admission':'personal')}>← Previous</button>}
                {formTab !== 'payment'    && <button className="btn btn-secondary" onClick={() => setFormTab(formTab==='personal'?'internship':formTab==='internship'?'admission':'payment')}>Next →</button>}
              </div>
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving...' : editId ? 'Update' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}

      {/* View Visit Modal */}
      {viewVisit && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setViewVisit(null)}>
          <div className="modal" style={{maxWidth:600}}>
            <div className="modal-header">
              <span className="modal-title">🏭 {viewVisit.college_name}</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setViewVisit(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:12}}>
                {[['Visit Date',viewVisit.visit_date],['Enquiry Date',viewVisit.enquiry_date],['Stream',viewVisit.stream],['Semester',viewVisit.semester],['Passout Year',viewVisit.passout_year],['Fees Collected',viewVisit.fees_collected?`₹${Number(viewVisit.fees_collected).toLocaleString('en-IN')}`:null],['Faculty/Class By',viewVisit.faculty_taken_class],['Coordinator',viewVisit.coordinator?.full_name],['Remarks',viewVisit.remarks]].map(([l,v])=>v?(
                  <div key={l} className="detail-row">
                    <div className="detail-label">{l}</div>
                    <div className="detail-value">{v}</div>
                  </div>
                ):null)}
              </div>
              <div style={{fontWeight:600,fontSize:13,marginBottom:8}}>👥 Students ({viewVisit.industrial_visit_students?.length||0})</div>
              {viewVisit.industrial_visit_students?.length>0?(
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                  <thead>
                    <tr style={{background:'var(--accent-light)'}}>
                      {['#','Name','Contact','Stream','Semester','Passout Year'].map(h=><th key={h} style={{padding:'5px 8px',textAlign:'left'}}>{h}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {viewVisit.industrial_visit_students.map((s,i)=>(
                      <tr key={s.id} style={{borderBottom:'1px solid var(--border)'}}>
                        <td style={{padding:'5px 8px',color:'var(--text3)'}}>{i+1}</td>
                        <td style={{padding:'5px 8px',fontWeight:500}}>{s.student_name}</td>
                        <td style={{padding:'5px 8px'}}>{s.contact_number||'—'}</td>
                        <td style={{padding:'5px 8px'}}>{s.stream||'—'}</td>
                        <td style={{padding:'5px 8px'}}>{s.semester||'—'}</td>
                        <td style={{padding:'5px 8px'}}>{s.passout_year||'—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ):<div style={{color:'var(--text3)',fontSize:12}}>No student details added</div>}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setViewVisit(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* New/Edit Visit Modal */}
      {showVisitModal && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setShowVisitModal(false)}>
          <div className="modal modal-xl">
            <div className="modal-header">
              <span className="modal-title">🏭 {editVisitId?'Edit':'New'} Industrial Visit</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setShowVisitModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{fontWeight:600,fontSize:13,marginBottom:10,color:'var(--accent)'}}>Visit Details</div>
              <div className="form-grid" style={{marginBottom:16}}>
                <div className="form-group">
                  <label>Centre *</label>
                  <select value={visitForm.centre} onChange={e=>setVisitForm(f=>({...f,centre:e.target.value}))}>
                    <option value="">Select centre</option>
                    <option value="EKM">EKM</option>
                    <option value="TVM">TVM</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>College Name *</label>
                  <select value={visitForm.college_id} onChange={e=>{
                    const col=colleges.find(c=>c.id===e.target.value);
                    setVisitForm(f=>({...f,college_id:e.target.value,college_name:col?.college_name||''}));
                  }}>
                    <option value="">Select college</option>
                    {colleges.map(c=><option key={c.id} value={c.id}>{c.college_name}</option>)}
                    <option value="__new__">+ Add new college...</option>
                  </select>
                  {visitForm.college_id==='__new__'&&(
                    <input style={{marginTop:4}} value={visitForm.college_name} onChange={e=>setVisitForm(f=>({...f,college_name:e.target.value}))} placeholder="Enter new college name"/>
                  )}
                </div>
                <div className="form-group">
                  <label>Visit Date *</label>
                  <DateInput value={visitForm.visit_date} onChange={e=>setVisitForm(f=>({...f,visit_date:e.target.value}))}/>
                </div>
                <div className="form-group">
                  <label>Enquiry Date</label>
                  <DateInput value={visitForm.enquiry_date} onChange={e=>setVisitForm(f=>({...f,enquiry_date:e.target.value}))}/>
                </div>
                <div className="form-group">
                  <label>Stream / Department</label>
                  <select value={visitForm.stream} onChange={e=>setVisitForm(f=>({...f,stream:e.target.value}))}>
                    <option value="">Select stream</option>
                    {['Mechanical','Civil','Electrical','Electronics','Computer Science','Chemical','Aeronautical','Other'].map(s=><option key={s} value={s}>{s}</option>)}
                  </select>
                </div>

                <div className="form-group">
                  <label>Semester</label>
                  <input value={visitForm.semester||''} onChange={e=>setVisitForm(f=>({...f,semester:e.target.value}))} placeholder="e.g. 6"/>
                </div>
                <div className="form-group">
                  <label>Passout Year</label>
                  <input value={visitForm.passout_year||''} onChange={e=>setVisitForm(f=>({...f,passout_year:e.target.value}))} placeholder="e.g. 2027"/>
                </div>
                <div className="form-group">
                  <label>Fees Collected (₹)</label>
                  <input type="number" value={visitForm.fees_collected} onChange={e=>setVisitForm(f=>({...f,fees_collected:e.target.value}))}/>
                </div>
                <div className="form-group">
                  <label>Class Taken By</label>
                  <input value={visitForm.faculty_taken_class} onChange={e=>setVisitForm(f=>({...f,faculty_taken_class:e.target.value}))} placeholder="Faculty/staff name"/>
                </div>
                <div className="form-group">
                  <label>Coordinator</label>
                  <select value={visitForm.coordinator_id} onChange={e=>setVisitForm(f=>({...f,coordinator_id:e.target.value}))}>
                    <option value="">Select staff</option>
                    {staffList.map(s=><option key={s.id} value={s.id}>{s.full_name}</option>)}
                  </select>
                </div>
                <div className="form-group full">
                  <label>Remarks</label>
                  <textarea rows={2} value={visitForm.remarks} onChange={e=>setVisitForm(f=>({...f,remarks:e.target.value}))}/>
                </div>
              </div>

              <div style={{fontWeight:600,fontSize:13,marginBottom:10,color:'var(--accent)'}}>
                Students ({visitStudents.length})
                <button className="btn btn-ghost btn-sm" style={{marginLeft:8,fontSize:11}} onClick={()=>setVisitStudents(s=>[...s,{ ...emptyVisitStudent }])}>+ Add Row</button>
              </div>
              <div style={{overflowX:'auto'}}>
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                  <thead>
                    <tr style={{background:'var(--surface2)'}}>
                      {['Name *','Contact','Stream','Semester','Passout Year',''].map(h=><th key={h} style={{padding:'5px 8px',textAlign:'left'}}>{h}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {visitStudents.map((s,i)=>(
                      <tr key={i}>
                        <td style={{padding:'4px'}}><input style={{width:'100%',fontSize:12}} value={s.student_name} onChange={e=>setVisitStudents(vs=>vs.map((v,j)=>j===i?{...v,student_name:e.target.value}:v))} placeholder="Student name"/></td>
                        <td style={{padding:'4px'}}><input style={{width:'100%',fontSize:12}} value={s.contact_number} onChange={e=>setVisitStudents(vs=>vs.map((v,j)=>j===i?{...v,contact_number:e.target.value}:v))} placeholder="Contact"/></td>
                        <td style={{padding:'4px'}}>
                          <select style={{width:'100%',fontSize:12}} value={s.stream} onChange={e=>setVisitStudents(vs=>vs.map((v,j)=>j===i?{...v,stream:e.target.value}:v))}>
                            <option value="">Stream</option>
                            {['Mechanical','Civil','Electrical','Electronics','Computer Science','Chemical','Aeronautical','Other'].map(st=><option key={st} value={st}>{st}</option>)}
                          </select>
                        </td>
                        <td style={{padding:'4px'}}><input style={{width:60,fontSize:12}} value={s.semester} onChange={e=>setVisitStudents(vs=>vs.map((v,j)=>j===i?{...v,semester:e.target.value}:v))} placeholder="Sem"/></td>
                        <td style={{padding:'4px'}}><input style={{width:70,fontSize:12}} value={s.passout_year} onChange={e=>setVisitStudents(vs=>vs.map((v,j)=>j===i?{...v,passout_year:e.target.value}:v))} placeholder="Year"/></td>
                        <td style={{padding:'4px'}}><button className="btn btn-ghost btn-sm" style={{color:'var(--red)'}} onClick={()=>setVisitStudents(vs=>vs.filter((_,j)=>j!==i))}>✕</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setShowVisitModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveVisit} disabled={savingVisit}>{savingVisit?'Saving...':'Save Visit'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
